import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TinyTimSettings = () => {
  const [health, setHealth] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [memories, setMemories] = useState([]);
  const [memoryStats, setMemoryStats] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeringBrief, setTriggeringBrief] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, g, t, w, m, s, b] = await Promise.all([
        axios.get(`${API}/tiny-tim/health`).catch(() => ({ data: null })),
        axios.get(`${API}/tiny-tim/oauth/status`).catch(() => ({ data: null })),
        axios.get(`${API}/tiny-tim/telegram/dm-status`).catch(() => ({ data: null })),
        axios.get(`${API}/tiny-tim/telegram/webhook-status`).catch(() => ({ data: null })),
        axios.get(`${API}/tiny-tim/long-term-memory?active_only=true&limit=50`).catch(() => ({ data: { memories: [] } })),
        axios.get(`${API}/tiny-tim/memory/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/tiny-tim/daily-brief/history?limit=5`).catch(() => ({ data: { briefs: [] } })),
      ]);
      setHealth(h.data);
      setGoogleStatus(g.data);
      setTelegramStatus(t.data);
      setWebhookStatus(w.data);
      setMemories(m.data?.memories || []);
      setMemoryStats(s.data);
      setBriefs(b.data?.briefs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const connectGoogle = async () => {
    window.open(`${API}/tiny-tim/oauth/connect`, '_blank', 'width=600,height=700');
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Gmail & Calendar from Tiny Tim?')) return;
    await axios.delete(`${API}/tiny-tim/oauth/disconnect`);
    fetchAll();
  };

  const triggerBrief = async () => {
    setTriggeringBrief(true);
    try {
      await axios.post(`${API}/tiny-tim/daily-brief/trigger`);
      fetchAll();
    } catch (e) { console.error(e); }
    setTriggeringBrief(false);
  };

  const deactivateMemory = async (memoryId) => {
    await axios.patch(`${API}/tiny-tim/long-term-memory/${memoryId}`);
    setMemories(prev => prev.filter(m => m.memory_id !== memoryId));
  };

  const fixWebhook = async () => {
    await axios.post(`${API}/tiny-tim/telegram/set-webhook`);
    fetchAll();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: 'linear-gradient(135deg, #00F0FF, #B026FF)', color: '#fff' }}>TT</div>
          <div>
            <h3 className="text-lg font-semibold text-white">Tiny Tim</h3>
            <p className="text-sm text-slate-400">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const StatusDot = ({ ok }) => (
    <span className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
  );

  return (
    <div className="space-y-6" data-testid="tiny-tim-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: 'linear-gradient(135deg, #00F0FF, #B026FF)', color: '#fff' }}>TT</div>
          <div>
            <h3 className="text-lg font-semibold text-white">Tiny Tim</h3>
            <p className="text-sm text-slate-400">AI Operations Assistant</p>
          </div>
        </div>
      </div>

      {/* Status Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* LLM Status */}
        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,240,255,0.1)' }}>
          <div className="flex items-center gap-2 mb-1">
            <StatusDot ok={health?.ollama_available} />
            <span className="text-xs font-medium text-slate-300">LLM</span>
          </div>
          <p className="text-sm text-white font-mono">
            {health?.ollama_available ? 'Ollama' : health?.fallback || 'Offline'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{health?.ollama_model}</p>
        </div>

        {/* Google */}
        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,240,255,0.1)' }}>
          <div className="flex items-center gap-2 mb-1">
            <StatusDot ok={googleStatus?.connected} />
            <span className="text-xs font-medium text-slate-300">Gmail / Calendar</span>
          </div>
          <p className="text-sm text-white">
            {googleStatus?.connected ? 'Connected' : 'Not connected'}
          </p>
          <button
            onClick={googleStatus?.connected ? disconnectGoogle : connectGoogle}
            data-testid="google-connect-btn"
            className="text-xs mt-1 transition-colors"
            style={{ color: googleStatus?.connected ? '#ef4444' : '#00F0FF' }}
          >
            {googleStatus?.connected ? 'Disconnect' : 'Connect'}
          </button>
        </div>

        {/* Telegram */}
        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,240,255,0.1)' }}>
          <div className="flex items-center gap-2 mb-1">
            <StatusDot ok={telegramStatus?.dm_registered} />
            <span className="text-xs font-medium text-slate-300">Telegram DM</span>
          </div>
          <p className="text-sm text-white">
            {telegramStatus?.dm_registered ? 'Registered' : 'Not registered'}
          </p>
          {telegramStatus?.dm_registered && (
            <p className="text-xs text-slate-500 mt-0.5">ID: {telegramStatus.chat_id}</p>
          )}
        </div>

        {/* Webhook */}
        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,240,255,0.1)' }}>
          <div className="flex items-center gap-2 mb-1">
            <StatusDot ok={webhookStatus?.url_correct} />
            <span className="text-xs font-medium text-slate-300">Webhook</span>
          </div>
          <p className="text-sm text-white">
            {webhookStatus?.url_correct ? 'Correct' : 'Mismatched'}
          </p>
          {!webhookStatus?.url_correct && (
            <button onClick={fixWebhook} className="text-xs mt-1" style={{ color: '#00F0FF' }}>
              Fix webhook
            </button>
          )}
        </div>
      </div>

      {/* Memory Stats */}
      {memoryStats && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,240,255,0.08)' }}>
          <h4 className="text-sm font-medium text-slate-300 mb-2">Memory</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div>
              <p className="text-xl font-bold font-mono" style={{ color: '#00F0FF' }}>{memoryStats.short_term_messages}</p>
              <p className="text-xs text-slate-500">Messages</p>
            </div>
            <div>
              <p className="text-xl font-bold font-mono" style={{ color: '#00F0FF' }}>{memoryStats.total_sessions}</p>
              <p className="text-xs text-slate-500">Sessions</p>
            </div>
            <div>
              <p className="text-xl font-bold font-mono" style={{ color: '#D279FF' }}>{memoryStats.long_term_active}</p>
              <p className="text-xs text-slate-500">Long-term</p>
            </div>
            <div>
              <p className="text-xl font-bold font-mono" style={{ color: '#D279FF' }}>{Object.keys(memoryStats.categories || {}).length}</p>
              <p className="text-xs text-slate-500">Categories</p>
            </div>
          </div>
        </div>
      )}

      {/* Long-term Memory / Rules */}
      <div className="p-4 rounded-lg" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,240,255,0.08)' }}>
        <h4 className="text-sm font-medium text-slate-300 mb-3">Rules & Memory ({memories.length})</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {memories.map((m) => (
            <div key={m.memory_id} className="flex items-start gap-2 p-2 rounded-lg group" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <span className="text-xs px-1.5 py-0.5 rounded font-mono flex-shrink-0 mt-0.5" style={{
                background: m.category === 'rule' ? 'rgba(0,240,255,0.1)' : 'rgba(176,38,255,0.1)',
                color: m.category === 'rule' ? '#00F0FF' : '#D279FF',
              }}>
                {m.category}
              </span>
              <p className="text-xs text-slate-300 leading-relaxed flex-1">{m.fact}</p>
              <button
                onClick={() => deactivateMemory(m.memory_id)}
                className="text-xs text-red-400/50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Deactivate"
              >
                x
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Brief History */}
      <div className="p-4 rounded-lg" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,240,255,0.08)' }}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-slate-300">Daily Briefs</h4>
          <button
            onClick={triggerBrief}
            disabled={triggeringBrief}
            data-testid="trigger-brief-btn"
            className="text-xs px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
            style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.2)', color: '#00F0FF' }}
          >
            {triggeringBrief ? 'Sending...' : 'Send now'}
          </button>
        </div>
        {briefs.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-4">No briefs yet. Scheduled daily at 9 AM EST.</p>
        ) : (
          <div className="space-y-2">
            {briefs.map((b, i) => (
              <details key={i} className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 hover:text-white transition-colors">
                  <StatusDot ok={b.sent} />
                  <span>{b.date}</span>
                  <span className="text-slate-600">|</span>
                  <span className="font-mono">{b.provider}</span>
                  <span className="text-slate-600">|</span>
                  <span>{(b.content || '').length} chars</span>
                </summary>
                <div className="mt-2 p-3 rounded-lg text-xs text-slate-300 whitespace-pre-wrap leading-relaxed" style={{ background: 'rgba(0,0,0,0.2)' }}>
                  {b.content}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TinyTimSettings;
